import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * Screens that are themselves keyboard-driven (the B2B billing console, where
 * focus sits on room <button>s and a stray "o" would navigate the operator out
 * mid-session) suspend the global bare-letter shortcuts while they are mounted.
 *
 * Call in an effect and run the returned cleanup on unmount:
 *   useEffect(() => suspendGlobalShortcuts(), [])
 */
let suspendCount = 0

export function suspendGlobalShortcuts(): () => void {
  suspendCount += 1
  let released = false

  return () => {
    if (released) return
    released = true
    suspendCount = Math.max(0, suspendCount - 1)
  }
}

export function useKeyboardShortcuts() {
  const navigate = useNavigate()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // A screen with its own key bindings has taken over.
      if (suspendCount > 0) return

      // Don't trigger when typing in inputs
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      // Navigation shortcuts (no modifier)
      if (e.key === 'd') navigate('/')           // Dashboard
      if (e.key === 'o') navigate('/orders')      // Orders
      if (e.key === 'a') navigate('/management?tab=agents')  // Agents
      if (e.key === 'c') navigate('/management?tab=customers') // Customers

      // Action shortcuts (Cmd/Ctrl + key)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        // Open global search (implement later or dispatch event)
        document.dispatchEvent(new CustomEvent('open-search'))
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault()
        // Open create order modal
        document.dispatchEvent(new CustomEvent('create-order'))
      }

      if (e.key === '?') {
        // Show shortcuts help modal
        document.dispatchEvent(new CustomEvent('show-shortcuts'))
      }

      if (e.key === 'Escape') {
        // Close any open panel/modal
        document.dispatchEvent(new CustomEvent('close-panel'))
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [navigate])
}
